import React, { useMemo, memo } from 'react';
import UnifiedCard from '../UnifiedCard';
import RecommendationExplanation from './RecommendationExplanation';
import { autoNormalizeCardData, CARD_CONTEXTS } from '../../utils/normalizeCardData';

/**
 * FeedCard — a single normalized UnifiedCard, memoized.
 *
 * Shared by the Home dashboard feed strips (FeedSection) and the Discover feed
 * (SocialFeed). Extracted so the per-card work — the autoNormalizeCardData object
 * rebuild and the buildActions(item) call — only re-runs when this specific item
 * changes, instead of on every parent re-render. On Discover this matters a lot:
 * the feed is an unbounded infinite-scroll list, so without this a single like
 * toggle would re-normalize and re-render every mounted card.
 *
 * Two things must be memoized for the surrounding memo() to actually hold:
 *  - normalizedData: autoNormalizeCardData returns a fresh object each call.
 *  - actions: buildActions(item) returns a fresh object each call (it's
 *    useCallback-stable on `modals`, but the returned object is new every time).
 *
 * @param {object}   item         Raw feed item.
 * @param {function} buildActions  Stable (useCallback) builder from useDiscoverCardActions.
 * @param {boolean}  showCreator   Whether to render the creator row.
 * @param {string}   cardContext   normalizeCardData context (defaults to DISCOVER).
 */
const FeedCard = memo(function FeedCard({
  item,
  buildActions,
  showCreator,
  // Discover only: one-tap Follow on a stranger's post.
  showFollow = false,
  cardContext = CARD_CONTEXTS.DISCOVER,
  // Pass a module-level constant to keep this memo() effective.
  imageSizing
}) {
  const normalizedData = useMemo(
    () => autoNormalizeCardData(item, cardContext),
    [item, cardContext]
  );

  const actions = useMemo(
    () => buildActions(item),
    [buildActions, item]
  );

  return (
    <>
    <RecommendationExplanation recommendation={normalizedData.recommendation} source={normalizedData.followSource} />
    <UnifiedCard
      data={normalizedData}
      actions={actions}
      showCreator={showCreator}
      showFollow={showFollow}
      imageSizing={imageSizing}
    />
    </>
  );
});

export default FeedCard;
